import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';

import { OfferSource } from '@cds';
import { Fixture } from '@cds/betting-offer';
import { FixtureFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { MarqueeConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent, ScrollSpeed } from '@frontend/sports/common/core/feature/scroll-adapter';
import { toCSSUrl } from '@frontend/sports/common/core/utils/css-formatters';
import { NumberDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { MarqueeBackgroundContainerConfig, TitleIconOptions } from '@frontend/sports/types/components/content';
import { MarqueeLink } from '@frontend/sports/types/components/widget/types';
import { clone, take } from 'lodash-es';

import { CountryFlagsConfigService } from '../common/country-flags-service';
import { MarqueeEventSubscriptionService } from '../event-subscription/marquee-event-subscription.service';
import { HeaderComponent } from './header.component';
import { MarqueeTitleIcon, OutrightTileData } from './marquee-tile.model';
import { OutrightsMarqueeCardComponent } from './outrights-marquee-card.component';

@Component({
    selector: 'ms-outrights-marquee',
    templateUrl: './outrights-marquee.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HeaderComponent, ScrollAdapterComponent, NgClass, OutrightsMarqueeCardComponent],
})
export class OutrightsMarqueeComponent implements OnInit, OnChanges, OnDestroy {
    private marqueeConfig = inject(MarqueeConfig);
    private fixtureFactory = inject(FixtureFactory);
    private marqueeEventSubscriptionService = inject(MarqueeEventSubscriptionService);
    private urlHelper = inject(UrlHelperService);
    private changeDetectorRef = inject(ChangeDetectorRef);
    private countryFlagsConfigService = inject(CountryFlagsConfigService);

    @Input() fixture: Fixture;
    @Input() backgroundContainerConfig: MarqueeBackgroundContainerConfig;
    @Input() globalLinkConfig: MarqueeLink;
    @Input() maxSelection?: number;
    @Input() scrollingSpeed: { [key: string]: string };
    @HostBinding('class.marquee-container') showBackgroundContainer = false;

    @ViewChild(ScrollAdapterComponent) scrollAdapter?: ScrollAdapterComponent;

    hasTiles: boolean;
    tiles: OutrightTileData[] = [];
    event: EventModel;
    outrightOptionGroup: NumberDictionary<EventOptionGroup>;
    scrollSpeed: ScrollSpeed;
    private eventSubscription?: EventSubscription<EventModel>;
    backgroundImage: string | undefined;
    titleIcon: MarqueeTitleIcon;
    globalLink: string;

    ngOnInit(): void {
        this.backgroundImage = this.backgroundContainerConfig?.backgroundImage ?? '';
    }

    ngOnChanges(changes: ISimpleChanges<OutrightsMarqueeComponent>): void {
        this.backgroundImage = toCSSUrl(this.backgroundContainerConfig?.backgroundImage ?? '');
        if (changes.fixture?.currentValue) {
            this.event = this.fixtureFactory.create(this.fixture);
            this.tiles = this.getTiles(this.fixture);
            this.outrightOptionGroup = this.mapOptionGroups(this.event, this.tiles);
            this.titleIcon = this.getDataForTitleIcon(this.fixture);
            this.setContainerContent(this.fixture);
            this.subscribeToEvent(this.event);

            this.refreshData();
            this.showBackgroundContainer =
                this.backgroundContainerConfig.showTitle &&
                this.backgroundContainerConfig.title !== undefined &&
                this.backgroundContainerConfig.title !== '' &&
                this.hasTiles;
            this.globalLink = this.getUrlFromFixture(this.fixture);
        }

        if (changes.fixture && this.scrollAdapter) {
            this.scrollAdapter.resetArrows();
        }
    }

    ngOnDestroy(): void {
        this.eventSubscription?.unsubscribe();
    }

    getListCardClass(): string {
        return this.backgroundContainerConfig.showTitle ? 'marquee-container-cards' : 'cards';
    }

    getMarqueeCardImgClass(): string {
        return this.backgroundImage !== '' && !!this.backgroundImage ? 'marquee-card-with-img' : 'marquee-card-without-img';
    }

    private setContainerContent(fixture: Fixture): void {
        if (this.backgroundContainerConfig.showTitle && !this.backgroundContainerConfig.title) {
            this.backgroundContainerConfig.title = this.getConcatenatedTitle(fixture);
        }

        if (this.globalLinkConfig.visible && !this.globalLinkConfig.linkOverride) {
            this.globalLinkConfig.linkOverride = this.getUrlFromFixture(fixture);
        }
    }

    private getConcatenatedTitle(fixture: Fixture): string {
        return `${fixture.name.value} ${this.isTV2Entity(fixture) ? fixture?.optionMarkets[0].name.value : fixture?.games[0]?.name.value}`;
    }

    private getTiles(fixture: Fixture): OutrightTileData[] {
        const tileData: OutrightTileData[] = [];
        let tileIndex = 0;
        if (!this.isTV2Entity(fixture)) {
            fixture?.games[0]?.results.forEach((result) => {
                tileData.push({
                    id: result.id,
                    index: tileIndex++,
                    odds: result.odds,
                    name: result.name.value,
                });
            });
        } else {
            fixture?.optionMarkets[0]?.options.forEach((option) => {
                tileData?.push({
                    id: option.id,
                    index: tileIndex++,
                    name: option.name.value,
                    odds: option.price.odds ?? 0,
                });
            });
        }
        const tiles = tileData?.sort((first, second) => first.odds - second.odds);
        tiles.forEach((item: OutrightTileData, index: number) => {
            item.index = index;
        });

        return take(tiles, this.maxSelection);
    }

    private isTV2Entity(fixture: Fixture): boolean {
        return fixture.source === OfferSource.V2;
    }

    private mapOptionGroups(event: EventModel, tiles: OutrightTileData[]): NumberDictionary<EventOptionGroup> {
        const optionGroups: NumberDictionary<EventOptionGroup> = {};

        for (const tile of tiles) {
            const optionGroupCloned = clone(event.optionGroups[0]);
            optionGroupCloned.options = optionGroupCloned.options.filter((o) => o.id === tile.id);
            optionGroups[tile.id] = optionGroupCloned;
        }

        return optionGroups;
    }

    private subscribeToEvent(event: EventModel): void {
        this.eventSubscription?.unsubscribe();
        if (!this.event) {
            return;
        }
        this.eventSubscription = this.marqueeEventSubscriptionService.subscribe([
            {
                event,
                callback: () => this.eventChanged(),
            },
        ]);
    }

    private eventChanged(): void {
        if (this.event) {
            this.fixture.games[0].results.map((result) => {
                result.odds = this.event.optionGroups[0].options.find((o) => o.id === result.id)?.nativePrice.odds.toNumber() ?? 0;
            });
            this.tiles = this.getTiles(this.fixture);
            this.outrightOptionGroup = this.mapOptionGroups(this.event, this.tiles);
            this.changeDetectorRef.detectChanges();
        }
    }

    private refreshData(): void {
        this.hasTiles = this.tiles.length !== 0;
        this.scrollSpeed = new ScrollSpeed(this.scrollingSpeed || this.marqueeConfig.scrollingSpeed);
    }

    private getUrlFromFixture(fixture: Fixture): string {
        const event = {
            id: fixture?.id,
            name: fixture?.name.value,
        };

        return this.urlHelper.getEventUrl(event);
    }

    private getDataForTitleIcon(fixture: Fixture): MarqueeTitleIcon {
        if (fixture) {
            switch (this.backgroundContainerConfig.titleIconDisplay) {
                case TitleIconOptions.SportIcon:
                    return {
                        id: fixture.sport.id.toString(),
                        title: fixture.sport?.name?.value,
                        icon: `sports-${fixture.sport.id}`,
                    };
                case TitleIconOptions.CompetitionLogo:
                case TitleIconOptions.CompetitionFlag:
                    return {
                        id: fixture.competition.id.toString(),
                        title: fixture.competition?.name?.value,
                        icon: this.countryFlagsConfigService.getFlagName(fixture.region.id.toString()),
                        sportId: fixture.sport.id,
                    };
            }
        }

        return { id: '', title: '' };
    }

    trackOutrightTile(index: number, tile: OutrightTileData): number {
        return tile.id;
    }
}
